import {
  faHourglassClock,
  faHourglassEnd,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssistChip, Button, Stack, Text } from '@sixui/ui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IApiInvitation } from '~/lib/api/modules/Invitations';
import { RelativeTime } from '~/components/atoms/RelativeTime';
import { Table } from '~/components/molecules/Table';
import { getOrganizationRoleLabel } from '~/components/organisms//fields/OrganizationRoleSelector';
import { useCancelInvitationMutation } from '~/lib/api/modules/Invitations/hooks/useCancelInvitationMutation';
import { useResendInvitationMutation } from '~/lib/api/modules/Invitations/hooks/useResendInvitationMutation';
import { getInvitationStatusLabel } from './getInvitationStatusLabel';

export type IOrganizationInvitationListItemProps = {
  organizationId: string;
  invitation: IApiInvitation;
};

export const OrganizationInvitationListItem: React.FC<
  IOrganizationInvitationListItemProps
> = (props) => {
  const { organizationId, invitation } = props;
  const { t } = useTranslation();
  const cancelInvitationMutation = useCancelInvitationMutation();
  const resendInvitationMutation = useResendInvitationMutation();
  const expired =
    invitation.status !== 'accepted' && invitation.expiredAt
      ? DateTime.fromISO(invitation.expiredAt).diffNow().milliseconds < 0
      : false;

  return (
    <Table.Row>
      <Table.Body>
        {invitation.method.type === 'email'
          ? invitation.method.emailAddress
          : 'N/A'}
      </Table.Body>
      <Table.Body>
        <AssistChip
          label={getOrganizationRoleLabel(invitation.target.role, t)}
        />
      </Table.Body>
      <Stack as={Table.Body} gap={1}>
        {/* TODO: use a tag component, or use a status component in the first column */}
        {getInvitationStatusLabel(expired ? 'expired' : invitation.status, t)}

        {invitation.expiredAt &&
        (expired || invitation.status === 'pending') ? (
          <Stack horizontal gap={2}>
            <FontAwesomeIcon
              icon={expired ? faHourglassEnd : faHourglassClock}
            />
            <RelativeTime
              i18nKey="OrganizationInvitationListItem.expires-in"
              isoDate={invitation.expiredAt}
            />
          </Stack>
        ) : null}
        {['accepted', 'declined', 'canceled'].includes(invitation.status) ? (
          <Text variant="body" size="sm" dimmed>
            <RelativeTime
              isoDate={invitation.updatedAt ?? invitation.createdAt}
            />
          </Text>
        ) : null}
      </Stack>
      <Stack as={Table.Body} horizontal justify="end">
        {invitation.status === 'pending' ? (
          <>
            <Button
              variant="text"
              onClick={() =>
                cancelInvitationMutation.mutateAsync({
                  organizationId,
                  invitationId: invitation._id,
                })
              }
              disabled={expired}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="text"
              onClick={() =>
                resendInvitationMutation.mutateAsync({
                  organizationId,
                  invitationId: invitation._id,
                })
              }
              disabled={expired}
            >
              {t('common.resend')}
            </Button>
          </>
        ) : null}
      </Stack>
    </Table.Row>
  );
};
